import type { ScoredGeography } from '@/types/insights';
import { getActiveAdapters } from '@/data/adapters';
import { buildMergedRecords } from '@/data/geographyJoin';
import { scoreAllGeographies } from '@/engine/scoring';

export interface PipelineResult {
  geographies: ScoredGeography[];
  warnings: string[];
  isSampleData: boolean;
}

/**
 * The full Phase 1 data flow in one place: fetch every enabled adapter in
 * parallel -> merge rows per geography -> score -> attach explanations.
 * A single failing/misconfigured adapter never aborts the others -- each
 * adapter contract guarantees it resolves with { rows: [], warnings }
 * rather than throwing.
 */
export async function runPipeline(signal?: AbortSignal): Promise<PipelineResult> {
  const adapters = getActiveAdapters();
  const warnings: string[] = [];
  let isSampleData = false;

  const fetches = await Promise.all(
    Object.values(adapters).map(async (adapter) => {
      try {
        const result = await adapter!.fetch({ signal });
        warnings.push(...result.warnings);
        if (result.isSampleData) isSampleData = true;
        return { sourceId: adapter!.meta.id, rows: result.rows };
      } catch (err) {
        warnings.push(`${adapter!.meta.id}: adapter threw unexpectedly: ${(err as Error).message}`);
        return { sourceId: adapter!.meta.id, rows: [] };
      }
    }),
  );

  const merged = buildMergedRecords(fetches);
  const geographies = scoreAllGeographies(merged, isSampleData);

  return { geographies, warnings, isSampleData };
}
